import React from 'react';
import { useState, useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as turf from '@turf/turf';
import { Table, Button, Modal, Form, Input, DatePicker, Select, Row, Col, Space, InputNumber } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { message } from 'antd';
import styled from '@emotion/styled';
import { createClient } from '@supabase/supabase-js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import moment from 'moment';

// Configure dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Bangkok'); // Set default timezone to Bangkok/Thailand

const MAPTILER_KEY = '8PIF0OZrNYjkmzNeepvV';

// Initialize Supabase client
const supabaseUrl = 'https://fesatjdlkyiwyaiwxlne.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZlc2F0amRsa3lpd3lhaXd4bG5lIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzcyNTUzNjQsImV4cCI6MjA1MjgzMTM2NH0.p4MNFppc90eN2W0tqb8dLYUMKkLDzW29jTn5LP1jI98';
const supabase = createClient(supabaseUrl, supabaseKey);

const initialDroneData = {
    droneId: '',
    droneCode: '',
    droneName: '',
    droneKPI: '',
    latitude: '',
    longitude: '',
    province: '',
    status: '',
    companyDrone: '',
    communityId: '',
    droneDate: null,
};

const initialFlyDroneData = {
    flyDroneId: '',
    flyDroneCode: '',
    droneId: '',
    serviceRequester: '',
    latitude: '',
    longitude: '',
    province: '',
    area: '',
    flyDate: null,
    duration: '',
    plant: '',
};

const PageHeader = styled.div`
  background: white;
  padding: 1rem 2rem;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);

  img {
    height: 41px;
    width: auto;
  }
`;

const StyledButton = styled(Button)`
  background: #165B33;
  border-color: #165B33;
  &:hover {
    background: #0D4423;
    border-color: #0D4423;
  }
`;

// ส่วนของ map หลักถูกลบออกแล้ว จึงไม่ต้องมี container สำหรับ main map
// const MapContainer = styled.div`
//   border-radius: 8px;
//   overflow: hidden;
//   box-shadow: 0 4px 12px rgba(0,0,0,0.08);
//   margin: 1rem 0;
// `;

const CoordinateCell = ({ coordinates, record }) => {
    // ส่วนนี้ยังคงใช้ maplibregl ในการแสดงแผนที่ย่อย (เช่น ภาพตัวอย่างในตาราง) ซึ่งไม่ได้เกี่ยวข้องกับ main map
    const mapId = `map-${record.id}`;

    useEffect(() => {
        if (!coordinates || coordinates.length < 3) return;

        const miniMap = new maplibregl.Map({
            container: mapId,
            style: `https://api.maptiler.com/maps/satellite/style.json?key=${MAPTILER_KEY}`,
            interactive: false,
            attributionControl: false,
        });

        const bounds = new maplibregl.LngLatBounds();
        const formattedCoords = coordinates.map(coord => {
            const [lat, lng] = coord;
            bounds.extend([lng, lat]);
            return [lng, lat];
        });
        formattedCoords.push(formattedCoords[0]);

        miniMap.on('load', () => {
            miniMap.addSource('polygon', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'Polygon',
                        coordinates: [formattedCoords],
                    },
                },
            });

            miniMap.addLayer({
                id: 'polygon-fill',
                type: 'fill',
                source: 'polygon',
                paint: {
                    'fill-color': '#165B33',
                    'fill-opacity': 0.3,
                },
            });

            miniMap.addLayer({
                id: 'polygon-outline',
                type: 'line',
                source: 'polygon',
                paint: {
                    'line-color': '#FFFFFF',
                    'line-width': 2,
                },
            });

            if (record.flightPath && record.flightPath.length > 1) {
                miniMap.addSource('flight-path', {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'LineString',
                            coordinates: record.flightPath,
                        },
                    },
                });

                miniMap.addLayer({
                    id: 'flight-path-line',
                    type: 'line',
                    source: 'flight-path',
                    paint: {
                        'line-color': '#FFFFFF',
                        'line-width': 2,
                        'line-dasharray': [2, 2],
                    },
                });
            }

            miniMap.fitBounds(bounds, {
                padding: 20,
                duration: 0,
            });
        });

        return () => miniMap.remove();
    }, [coordinates, record.id, record.flightPath, mapId]);

    return (
        <div style={{ display: 'flex', gap: '16px' }}>
            <div
                id={mapId}
                style={{
                    width: '200px',
                    height: '150px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}
            />
        </div>
    );
};

const LoginModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    background: transparent;
    box-shadow: none;
  }
  .ant-modal-header {
    background: #165B33;
    padding: 16px 24px;
    border-bottom: none;
  }
  .ant-modal-title {
    color: white;
    font-size: 18px;
  }
  .ant-modal-body {
    padding: 24px;
    background: white;
    border-radius: 0 0 12px 12px;
  }
`;

const BlurOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginContainer = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

export default function AdminPage() {
    // main map (และ container ของมัน) ถูกลบออกแล้ว
    // const mapContainer = useRef(null);
    // const map = useRef(null);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [drones, setDrones] = useState([]);
    const [form] = Form.useForm();
    // state สำหรับ form map ยังคงอยู่
    const formMapContainer = useRef(null);
    const formMap = useRef(null);
    const [formSearchCoord, setFormSearchCoord] = useState('');
    const [formArea, setFormArea] = useState(0);
    const [points, setPoints] = useState([]);
    const pointLayerIds = useRef([]);
    // ลบ mainMapPoints และ mainMapPointLayerIds ออกไป
    const [flightPath, setFlightPath] = useState([]);
    // ลบ state ที่เกี่ยวข้องกับ main map flight path ออกไป
    // const [flightPathVisible, setFlightPathVisible] = useState(false);
    // const [activeFlightPath, setActiveFlightPath] = useState(null);
    // const [activeDroneId, setActiveDroneId] = useState(null);
    // const [isDroneFlying, setIsDroneFlying] = useState(false);
    const [droneSpeed, setDroneSpeed] = useState(5); // Default speed 5 m/s
    const [loading, setLoading] = useState(true);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [droneToDelete, setDroneToDelete] = useState(null);
    const [isLoginModalVisible, setIsLoginModalVisible] = useState(true);
    const [loginForm] = Form.useForm();
    const [userRole, setUserRole] = useState(null);
    const [rowSpacing, setRowSpacing] = useState(5); // Default 5 meters
    const [flightAngle, setFlightAngle] = useState(0); // Default 0 degrees

    // ตรวจสอบ session storage เมื่อ component mount
    useEffect(() => {
        const storedRole = sessionStorage.getItem('userRole');
        if (storedRole) {
            setUserRole(storedRole);
            setIsLoginModalVisible(false);
        }
    }, []);

    const handleLogin = async (values) => {
        const { username, password } = values;

        let role = null;
        if (username === 'admin' && password === 'SeGuMo5599') {
            role = 'admin';
        } else if (username === 'depa' && password === 'SGM2024') {
            role = 'viewer';
        } else {
            message.error('Invalid credentials');
            return;
        }

        sessionStorage.setItem('userRole', role);
        setUserRole(role);
        setIsLoginModalVisible(false);
        message.success(`Welcome, ${username}!`);
    };

    const handleLogout = () => {
        sessionStorage.removeItem('userRole');
        setUserRole(null);
        setIsLoginModalVisible(true);
        message.success('Logged out successfully');
    };

    const HeaderContent = () => (
        <>
            <img
                src="https://segumodrone.com/wp-content/uploads/2021/03/SEGUMO-ENG-green165x41-Color.png.webp"
                alt="Segumo Drone"
            />
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>

                {userRole === 'admin' && (
                    <StyledButton type="primary" onClick={handleAddNew} icon={<PlusOutlined />}>
                        Add Drone
                    </StyledButton>
                )}
                <Button type="default" onClick={handleLogout}>
                    Logout
                </Button>
            </div>
        </>
    );

    // ปรับปรุง droneColumns โดยเอาปุ่ม "View on Map" ที่เกี่ยวกับ main map ออกไป
    const droneColumns = [
        {
            title: 'Drone Date',
            dataIndex: 'droneDate',
            key: 'droneDate',
            render: (text) =>
                dayjs(text.replace("+00:00", "+07:00"))
                    .tz('Asia/Bangkok')
                    .format('DD/MM/YYYY HH:mm:ss'),
            sorter: (a, b) =>
                moment(a.droneDate.replace("+00:00", "+07:00")).unix() -
                moment(b.droneDate.replace("+00:00", "+07:00")).unix(),
            filters: Array.from(new Set(drones.map((item) =>
                moment(item.droneDate.replace("+00:00", "+07:00")).format("DD/MM/YYYY")))).map(
                    (date) => ({ text: date, value: date })
                ),
            onFilter: (value, record) =>
                moment(record.droneDate.replace("+00:00", "+07:00")).format("DD/MM/YYYY") === value,
        },
        {
            title: 'Drone ID',
            dataIndex: 'droneId',
            key: 'droneId',
            sorter: (a, b) => a.droneId.localeCompare(b.droneId),
            filters: Array.from(new Set(drones.map((item) => item.droneId)))
                .filter(id => id)
                .map(id => ({ text: id, value: id })),
            onFilter: (value, record) => record.droneId === value,
        },
        {
            title: 'Drone Code',
            dataIndex: 'droneCode',
            key: 'droneCode',
            sorter: (a, b) => a.droneCode.localeCompare(b.droneCode)
        },
        {
            title: 'Drone Name',
            dataIndex: 'droneName',
            key: 'droneName',
            sorter: (a, b) => a.droneName.localeCompare(b.droneName)
        },
        {
            title: 'Drone KPI',
            dataIndex: 'droneKPI',
            key: 'droneKPI',
            sorter: (a, b) => a.droneKPI - b.droneKPI
        },
        {
            title: 'Community ID',
            dataIndex: 'communityId',
            key: 'communityId',
            sorter: (a, b) => {
                if (!a.communityId) return -1;
                if (!b.communityId) return 1;
                return a.communityId.localeCompare(b.communityId);
            },
            filters: Array.from(new Set(drones.map((item) => item.communityId)))
                .filter(id => id)
                .map(id => ({ 
                    text: id, 
                    value: id 
                })),
            onFilter: (value, record) => record.communityId === value,
            render: (text) => text || '-'
        },
        {
            title: 'Area (ไร่)',
            dataIndex: 'area',
            key: 'area',
            sorter: (a, b) => a.area - b.area
        },
        {
            title: 'Duration (นาที)',
            dataIndex: 'duration',
            key: 'duration',
            sorter: (a, b) => a.duration - b.duration
        },
        {
            title: 'Speed (m/s)',
            dataIndex: 'droneSpeed',
            key: 'droneSpeed',
            sorter: (a, b) => a.droneSpeed - b.droneSpeed
        },
        {
            title: 'Province',
            dataIndex: 'province',
            key: 'province',
            sorter: (a, b) => a.province.localeCompare(b.province),
            filters: Array.from(new Set(drones.map((item) => item.province))).map(
                (province) => ({ text: province, value: province })
            ),
            onFilter: (value, record) => record.province === value,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a, b) => a.status.localeCompare(b.status),
            filters: Array.from(new Set(drones.map((item) => item.status))).map(
                (status) => ({ text: status, value: status })
            ),
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Company Drone',
            dataIndex: 'companyDrone',
            key: 'companyDrone',
            sorter: (a, b) => a.companyDrone.localeCompare(b.companyDrone)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) =>
                userRole === 'admin' ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                            type="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditingRecord(record);
                                form.setFieldsValue(record);
                                setPoints(record.coordinates?.map(([lat, lng]) => [lng, lat]) || []);
                                setFormArea(record.area || 0);
                                setFlightPath(record.flightPath || []);
                                setIsModalVisible(true);
                            }}
                            icon={<EditOutlined />}
                        >
                            Edit
                        </Button>
                        <Button
                            type="link"
                            danger
                            onClick={(e) => {
                                e.stopPropagation();
                                setDroneToDelete(record);
                                setDeleteModalVisible(true);
                            }}
                            icon={<DeleteOutlined />}
                        >
                            Delete
                        </Button>
                    </div>
                ) : (
                    <div />
                ),
        },
    ];

    const calculateArea = (coordinates) => {
        if (!coordinates || coordinates.length < 3) return 0;
        let closedCoordinates = [...coordinates];
        if (JSON.stringify(coordinates[0]) !== JSON.stringify(coordinates[coordinates.length - 1])) {
            closedCoordinates.push(coordinates[0]);
        }
        const poly = turf.polygon([closedCoordinates]);
        const areaInRai = turf.area(poly) / 1600;
        return areaInRai.toFixed(2);
    };

    const handleFormMapClick = useRef((e) => {
        const { lng, lat } = e.lngLat;
        setPoints(prevPoints => {
            const newPoints = [...prevPoints, [lng, lat]];
            console.log('Adding new point:', newPoints);
            const coordinates = newPoints.map(([lng, lat]) => [lat, lng]);
            form.setFieldsValue({ coordinates });
            if (formMap.current) {
                try {
                    const pointId = `point-${Date.now()}`;
                    pointLayerIds.current.push(pointId);
                    formMap.current.addSource(pointId, {
                        type: 'geojson',
                        data: {
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: [lng, lat]
                            }
                        }
                    });
                    formMap.current.addLayer({
                        id: pointId,
                        type: 'circle',
                        source: pointId,
                        paint: {
                            'circle-radius': 6,
                            'circle-color': '#165B33',
                            'circle-stroke-color': '#FFFFFF',
                            'circle-stroke-width': 2
                        }
                    });
                    if (newPoints.length > 1) {
                        drawLines(newPoints);
                    }
                    if (newPoints.length >= 3) {
                        calculateAreaFromPoints(newPoints);
                    }
                } catch (error) {
                    console.error('Error adding point:', error);
                    message.error('เกิดข้อผิดพลาดในการเพิ่มจุด');
                }
            }
            return newPoints;
        });
    }).current;

    const drawLines = (currentPoints) => {
        if (!formMap.current) return;
        if (formMap.current.getLayer('line-layer')) {
            formMap.current.removeLayer('line-layer');
            formMap.current.removeSource('line-source');
        }
        let lineCoordinates = [...currentPoints];
        if (currentPoints.length >= 3) {
            lineCoordinates.push(currentPoints[0]);
        }
        try {
            formMap.current.addSource('line-source', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: lineCoordinates
                    }
                }
            });
            formMap.current.addLayer({
                id: 'line-layer',
                type: 'line',
                source: 'line-source',
                paint: {
                    'line-color': '#FFFFFF',
                    'line-width': 2
                }
            });
        } catch (error) {
            console.error('Error drawing lines:', error);
        }
    };

    const calculateAreaFromPoints = (currentPoints) => {
        if (currentPoints.length < 3) return;
        try {
            const coordinates = currentPoints.map(point => point);
            coordinates.push(coordinates[0]);
            const polygon = turf.polygon([coordinates]);
            const area = turf.area(polygon);
            const areaInRai = area / 1600;
            setFormArea(areaInRai.toFixed(2));
            form.setFieldsValue({ area: areaInRai.toFixed(2) });
            console.log(`คำนวณพื้นที่: ${areaInRai.toFixed(2)} ไร่`);
        } catch (error) {
            console.error('Error calculating area:', error);
        }
    };

    const clearFormMap = useRef(() => {
        console.log('Clearing map...');
        try {
            pointLayerIds.current.forEach(pointId => {
                try {
                    if (formMap.current?.getLayer(pointId)) {
                        formMap.current.removeLayer(pointId);
                    }
                    if (formMap.current?.getSource(pointId)) {
                        formMap.current.removeSource(pointId);
                    }
                } catch (error) {
                    console.error('Error removing point:', pointId, error);
                }
            });
            try {
                if (formMap.current?.getLayer('line-layer')) {
                    formMap.current.removeLayer('line-layer');
                }
                if (formMap.current?.getSource('line-source')) {
                    formMap.current.removeSource('line-source');
                }
            } catch (error) {
                console.error('Error removing line layer:', error);
            }
            pointLayerIds.current = [];
            setPoints([]);
            setFormArea(0);
            form.setFieldsValue({
                area: '',
                coordinates: [],
                latitude: '',
                longitude: ''
            });
            try {
                if (formMap.current?.getLayer('flight-path')) {
                    formMap.current.removeLayer('flight-path');
                    formMap.current.removeSource('flight-path-source');
                }
            } catch (error) {
                console.error('Error removing flight path:', error);
            }
            setFlightPath([]);
        } catch (error) {
            console.error('Error in clearFormMap:', error);
        }
    }).current;

    const handleFormSearch = () => {
        if (!formSearchCoord) {
            message.error('กรุณากรอกพิกัด');
            return;
        }
        try {
            const [lat, lng] = formSearchCoord.split(',').map(coord => parseFloat(coord.trim()));
            if (isNaN(lat) || isNaN(lng)) {
                message.error('รูปแบบพิกัดไม่ถูกต้อง');
                return;
            }
            formMap.current.flyTo({
                center: [lng, lat],
                zoom: 15
            });
        } catch (error) {
            message.error('รูปแบบพิกัดไม่ถูกต้อง');
        }
    };

    const handleModalCancel = () => {
        try {
            setIsModalVisible(false);
            form.resetFields();
            clearFormMap();
            setPoints([]);
            setFormArea(0);
        } catch (error) {
            console.error('Error in handleModalCancel:', error);
        }
    };

    const handleAddNew = () => {
        try {
            setEditingRecord(null);
            form.resetFields();
            setPoints([]);
            setFormArea(0);
            setIsModalVisible(true);
        } catch (error) {
            console.error('Error in handleAddNew:', error);
            message.error('เกิดข้อผิดพลาดในการเพิ่มข้อมูลใหม่');
        }
    };

    // useEffect สำหรับ form map ยังคงอยู่
    useEffect(() => {
        if (!isModalVisible || !formMapContainer.current) return;
        try {
            if (formMap.current) {
                try {
                    clearFormMap();
                    formMap.current.remove();
                } catch (error) {
                    console.error('Error removing old form map:', error);
                }
                formMap.current = null;
            }
            formMap.current = new maplibregl.Map({
                container: formMapContainer.current,
                style: `https://api.maptiler.com/maps/satellite/style.json?key=${MAPTILER_KEY}`,
                center: [100.5018, 13.7563],
                zoom: 10
            });
            formMap.current.addControl(new maplibregl.NavigationControl());
            formMap.current.on('load', () => {
                formMap.current.on('click', handleFormMapClick);
            });
            return () => {
                if (formMap.current) {
                    try {
                        formMap.current.off('click', handleFormMapClick);
                        formMap.current.remove();
                    } catch (error) {
                        console.error('Error in form map cleanup:', error);
                    }
                    formMap.current = null;
                }
            };
        } catch (error) {
            console.error('Error in form map useEffect:', error);
            message.error('เกิดข้อผิดพลาดในการโหลดแผนที่ฟอร์ม');
        }
    }, [isModalVisible]);

    const calculateFlightDuration = (pathCoordinates, speed) => {
        try {
            if (!pathCoordinates || pathCoordinates.length < 2 || !speed || speed <= 0) {
                console.log('Invalid path coordinates or speed');
                return '0 นาที 00 วินาที';
            }
            
            let totalDistance = 0;
            // คำนวณระยะทางรวมของเส้นทางการบิน
            for (let i = 0; i < pathCoordinates.length - 1; i++) {
                const from = turf.point(pathCoordinates[i]);
                const to = turf.point(pathCoordinates[i + 1]);
                const distance = turf.distance(from, to, { units: 'meters' });
                totalDistance += distance;
            }
            
            console.log('Total distance:', totalDistance, 'meters');
            console.log('Speed:', speed, 'm/s');
            
            // คำนวณเวลาที่ใช้ในการบิน
            const durationSeconds = totalDistance / speed;
            const minutes = Math.floor(durationSeconds / 60);
            const seconds = Math.round(durationSeconds % 60);
            
            const duration = `${minutes} นาที ${seconds.toString().padStart(2, '0')} วินาที`;
            console.log('Calculated duration:', duration);
            return duration;
        } catch (error) {
            console.error('Error calculating duration:', error);
            return '0 นาที 00 วินาที';
        }
    };

    const handleCalculateDuration = () => {
        const speed = form.getFieldValue('droneSpeed');
        if (!speed || speed <= 0) {
            message.warning('กรุณาระบุความเร็วของโดรน');
            return;
        }
        if (!flightPath || flightPath.length < 2) {
            message.warning('กรุณาสร้างเส้นทางการบินก่อน');
            return;
        }
        
        console.log('Calculating duration with:');
        console.log('Flight path:', flightPath);
        console.log('Speed:', speed);
        
        const duration = calculateFlightDuration(flightPath, speed);
        form.setFieldsValue({ duration });
        message.success(`คำนวณระยะเวลาบินสำเร็จ: ${duration}`);
    };

    const generateFlightPath = () => {
        if (points.length < 3) {
            message.warning('ต้องมีอย่างน้อย 3 จุดเพื่อสร้างเส้นทางการบิน');
            return;
        }
        try {
            // สร้าง polygon จากจุดที่ผู้ใช้กำหนด
            const coordinates = [...points];
            if (coordinates[0] !== coordinates[coordinates.length - 1]) {
                coordinates.push(coordinates[0]); // ปิด polygon โดยเพิ่มจุดแรกต่อท้าย
            }
            const polygon = turf.polygon([coordinates]);
            const bbox = turf.bbox(polygon);
            const [minX, minY, maxX, maxY] = bbox;
            const center = turf.center(polygon);
            const centerLat = center.geometry.coordinates[1];
            const metersPerDegreeLng = Math.cos(centerLat * Math.PI / 180) * 111111;
            const spacingLng = rowSpacing / metersPerDegreeLng;
            const width = maxX - minX;
            const height = maxY - minY;
            const diagonal = Math.sqrt(width * width + height * height);
            const lines = [];
            const angleRad = (flightAngle * Math.PI) / 180;
            const dirX = Math.cos(angleRad);
            const dirY = Math.sin(angleRad);
            const perpX = Math.cos(angleRad + Math.PI / 2);
            const perpY = Math.sin(angleRad + Math.PI / 2);

            // สร้างเส้นทางการบินแบบ zigzag
            for (let d = -diagonal; d <= diagonal; d += spacingLng) {
                const offsetX = perpX * d;
                const offsetY = perpY * d;
                const lineStart = [
                    center.geometry.coordinates[0] + offsetX - dirX * diagonal,
                    center.geometry.coordinates[1] + offsetY - dirY * diagonal
                ];
                const lineEnd = [
                    center.geometry.coordinates[0] + offsetX + dirX * diagonal,
                    center.geometry.coordinates[1] + offsetY + dirY * diagonal
                ];
                const line = turf.lineString([lineStart, lineEnd]);
                const intersection = turf.lineIntersect(line, polygon);
                if (intersection.features.length >= 2) {
                    lines.push([
                        intersection.features[0].geometry.coordinates,
                        intersection.features[1].geometry.coordinates
                    ]);
                }
            }

            // สร้างเส้นทางการบินที่ต่อเนื่อง
            const path = [];
            let reverse = false;
            lines.forEach((line, index) => {
                const coords = reverse ? [...line].reverse() : line;
                if (index > 0) {
                    path.push(coords[0]); // เพิ่มจุดเชื่อมต่อระหว่างเส้น
                }
                path.push(...coords);
                reverse = !reverse;
            });

            // แสดงเส้นทางการบินบนแผนที่
            if (formMap.current) {
                if (formMap.current.getLayer('flight-path')) {
                    formMap.current.removeLayer('flight-path');
                }
                if (formMap.current.getSource('flight-path-source')) {
                    formMap.current.removeSource('flight-path-source');
                }
                formMap.current.addSource('flight-path-source', {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: path
                        }
                    }
                });
                formMap.current.addLayer({
                    id: 'flight-path',
                    type: 'line',
                    source: 'flight-path-source',
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                        visibility: 'visible'
                    },
                    paint: {
                        'line-color': '#FFFFFF',
                        'line-width': 3,
                        'line-dasharray': [2, 2]
                    }
                });
            }

            // บันทึกเส้นทางการบินและคำนวณระยะเวลา
            setFlightPath(path);
            const speed = form.getFieldValue('droneSpeed');
            if (speed && path.length > 1) {
                const duration = calculateFlightDuration(path, speed);
                form.setFieldsValue({ duration });
                console.log('Flight path generated:', path);
                console.log('Speed:', speed);
                console.log('Duration calculated:', duration);
                message.success(`สร้างเส้นทางการบินสำเร็จ (ระยะเวลาบิน: ${duration})`);
            } else {
                message.warning('กรุณาระบุความเร็วของโดรนเพื่อคำนวณระยะเวลาบิน');
            }
        } catch (error) {
            console.error('Error generating flight path:', error);
            message.error('เกิดข้อผิดพลาดในการสร้างเส้นทางการบิน');
        }
    };

    const handleDroneSearch = async (searchDroneId) => {
        try {
            const { data, error } = await supabase
                .from('drones')
                .select('*')
                .eq('droneId', searchDroneId)
                .limit(1);
            if (error) {
                message.error('Drone not found');
                return;
            }
            if (data) {
                form.setFieldsValue({
                    droneId: data[0].droneId,
                    droneCode: data[0].droneCode,
                    droneName: data[0].droneName,
                    droneKPI: data[0].droneKPI,
                    province: data[0].province,
                    status: data[0].status,
                    companyDrone: data[0].companyDrone,
                    communityId: data[0].communityId,
                });
                message.success('Drone information loaded');
            }
        } catch (error) {
            console.error('Error searching for drone:', error);
            message.error('Error searching for drone');
        }
    };

    const calculateTotalArea = async (droneId) => {
        try {
            const { data, error } = await supabase
                .from('drones')
                .select('area')
                .eq('droneId', droneId);
            if (error) {
                message.error('Error fetching drone data');
                return;
            }
            if (data && data.length > 0) {
                const totalArea = data.reduce((sum, record) => {
                    return sum + (record.area || 0);
                }, 0);
                Modal.info({
                    title: 'Total Area Calculation',
                    content: (
                        <div>
                            <p>Drone ID: {droneId}</p>
                            <p>จำนวนพื้นที่ทั้งหมด: {totalArea.toFixed(2)} ไร่</p>
                            <p>จำนวนเที่ยวบิน: {data.length} เที่ยว</p>
                        </div>
                    ),
                });
            } else {
                message.warning('ไม่พบข้อมูลของ Drone ID นี้');
            }
        } catch (error) {
            console.error('Error calculating total area:', error);
            message.error('เกิดข้อผิดพลาดในการคำนวณพื้นที่');
        }
    };

    const AreaCalculator = () => {
        const [calculatorDroneId, setCalculatorDroneId] = useState('');
        return (
            <div className="mb-4 p-4 bg-white rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-3">คำนวณพื้นที่รวม</h3>
                <Space>
                    <Input
                        placeholder="ระบุ Drone ID"
                        value={calculatorDroneId}
                        onChange={(e) => setCalculatorDroneId(e.target.value)}
                        style={{ width: '200px' }}
                    />
                    <Button
                        type="primary"
                        onClick={() => calculateTotalArea(calculatorDroneId)}
                        disabled={!calculatorDroneId}
                    >
                        คำนวณพื้นที่
                    </Button>
                </Space>
            </div>
        );
    };

    const AreaSummaryTable = () => {
        const [summaryData, setSummaryData] = useState([]);
        const [loading, setLoading] = useState(false);
        const [filters, setFilters] = useState({});
        const [sorter, setSorter] = useState({});

        const handleEditClick = async (record) => {
            try {
                // ดึงข้อมูลทั้งหมดของ drone จาก Supabase
                const { data, error } = await supabase
                    .from('drones')
                    .select('*')
                    .eq('droneId', record.droneId)
                    .order('droneDate', { ascending: false })
                    .limit(1)
                    .single();

                if (error) throw error;

                if (data) {
                    // เตรียมข้อมูลสำหรับ modal
                    setEditingRecord(data);
                    form.setFieldsValue({
                        ...data,
                        droneDate: data.droneDate ? dayjs(data.droneDate) : null
                    });
                    setPoints(data.coordinates?.map(([lat, lng]) => [lng, lat]) || []);
                    setFormArea(data.area || 0);
                    setFlightPath(data.flightPath || []);
                    setIsModalVisible(true);
                }
            } catch (error) {
                console.error('Error fetching drone data:', error);
                message.error('Failed to fetch drone data');
            }
        };

        const handleDeleteClick = async (record) => {
            try {
                // ดึงข้อมูลทั้งหมดของ drone จาก Supabase
                const { data, error } = await supabase
                    .from('drones')
                    .select('*')
                    .eq('droneId', record.droneId)
                    .order('droneDate', { ascending: false })
                    .limit(1)
                    .single();

                if (error) throw error;

                if (data) {
                    setDroneToDelete(data);
                    setDeleteModalVisible(true);
                }
            } catch (error) {
                console.error('Error fetching drone data:', error);
                message.error('Failed to fetch drone data');
            }
        };

        const columns = [
            {
                title: 'Drone ID',
                dataIndex: 'droneId',
                key: 'droneId',
                sorter: true
            },
            {
                title: 'Community ID',
                dataIndex: 'communityId',
                key: 'communityId',
                sorter: (a, b) => {
                    if (!a.communityId) return -1;
                    if (!b.communityId) return 1;
                    return a.communityId.localeCompare(b.communityId);
                },
                filters: Array.from(new Set(summaryData
                    .map(item => item.communityId)
                    .filter(id => id)))
                    .map(id => ({ 
                        text: id, 
                        value: id 
                    })),
                onFilter: (value, record) => record.communityId === value,
                render: (text) => text || '-'
            },
            {
                title: 'จำนวนพื้นที่ทั้งหมด (ไร่)',
                dataIndex: 'totalArea',
                key: 'totalArea',
                render: (text) => Number(text).toFixed(2),
                sorter: (a, b) => a.totalArea - b.totalArea
            },
            {
                title: 'จำนวนเที่ยวบิน',
                dataIndex: 'flightCount',
                key: 'flightCount',
                sorter: (a, b) => a.flightCount - b.flightCount
            },
            
        ];

        const fetchSummaryData = async (params = {}) => {
            setLoading(true);
            try {
                let allData = [];
                let start = 0;
                const pageSize = 1000;
                let hasMore = true;

                while (hasMore) {
                    let query = supabase
                        .from('drones')
                        .select('*', { count: 'exact' })
                        .range(start, start + pageSize - 1);

                    const { data, error, count } = await query;
                    if (error) throw error;

                    if (data && data.length > 0) {
                        allData = [...allData, ...data];
                    }

                    start += pageSize;
                    hasMore = start < count;
                }

                // สร้างตารางสรุปจากข้อมูลทั้งหมด
                const summary = allData.reduce((acc, curr) => {
                    if (!curr.droneId) return acc;
                    if (!acc[curr.droneId]) {
                        acc[curr.droneId] = {
                            droneId: curr.droneId,
                            communityId: curr.communityId,
                            totalArea: 0,
                            flightCount: 0
                        };
                    }
                    acc[curr.droneId].totalArea += Number(curr.area) || 0;
                    acc[curr.droneId].flightCount += 1;
                    return acc;
                }, {});

                // แปลงเป็น array และเรียงลำดับตามที่ต้องการ
                let sortedData = Object.values(summary);
                if (params.sorter && params.sorter.field) {
                    const { field, order } = params.sorter;
                    sortedData.sort((a, b) => {
                        if (order === 'descend') {
                            return b[field] - a[field];
                        }
                        return a[field] - b[field];
                    });
                }

                setSummaryData(sortedData);
            } catch (error) {
                console.error('Error fetching summary data:', error);
                message.error('Failed to fetch summary data');
            } finally {
                setLoading(false);
            }
        };

        useEffect(() => {
            fetchSummaryData({});
        }, []);

        const handleTableChange = (_, newFilters, newSorter) => {
            setFilters(newFilters);
            setSorter(newSorter);
            fetchSummaryData({
                sorter: newSorter
            });
        };

        const downloadSummaryCSV = () => {
            try {
                // Define headers for summary CSV
                const headers = [
                    'Drone ID',
                    'Community ID',
                    'จำนวนพื้นที่ทั้งหมด (ไร่)',
                    'จำนวนเที่ยวบิน'
                ];

                // Sort data by total area in descending order
                const sortedData = [...summaryData].sort((a, b) => b.totalArea - a.totalArea);

                // Convert sorted summary data to CSV format
                const csvData = sortedData.map(data => [
                    data.droneId,
                    data.communityId,
                    Number(data.totalArea).toFixed(2),
                    data.flightCount
                ]);

                // Add headers to CSV data
                csvData.unshift(headers);

                // Convert to CSV string
                const csvString = csvData
                    .map(row => row.map(cell => `"${cell || ''}"`).join(','))
                    .join('\n');

                // Create blob and download
                const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `drone_area_summary_${dayjs().format('YYYY-MM-DD')}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                message.success('Summary CSV file downloaded successfully');
            } catch (error) {
                console.error('Error downloading summary CSV:', error);
                message.error('Failed to download summary CSV file');
            }
        };

        return (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 className="text-xl font-semibold" style={{ color: '#165B33', margin: 0 }}>
                        สรุปพื้นที่ตาม Drone ID
                    </h2>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadSummaryCSV}
                        style={{ background: '#165B33' }}
                    >
                        Download Summary
                    </Button>
                </div>
                <Table
                    columns={columns}
                    dataSource={summaryData}
                    loading={loading}
                    rowKey="droneId"
                    size="middle"
                    onChange={handleTableChange}
                />
            </div>
        );
    };

    const DroneList = () => {
        const [droneData, setDroneData] = useState([]);
        const [loading, setLoading] = useState(false);

        const fetchDroneData = async () => {
            setLoading(true);
            try {
                let allData = [];
                let start = 0;
                const pageSize = 1000;
                let hasMore = true;

                while (hasMore) {
                    const { data, error, count } = await supabase
                        .from('drones')
                        .select('*', { count: 'exact' })
                        .range(start, start + pageSize - 1)
                        .order('droneDate', { ascending: false });

                    if (error) throw error;

                    if (data) {
                        allData = [...allData, ...data];
                    }

                    start += pageSize;
                    hasMore = start < count;
                }

                setDroneData(allData);
            } catch (error) {
                console.error('Error fetching drone data:', error);
                message.error('Failed to fetch drone data');
            } finally {
                setLoading(false);
            }
        };

        useEffect(() => {
            fetchDroneData();
        }, []);

        const columns = [
            {
                title: 'Drone Date',
                dataIndex: 'droneDate',
                key: 'droneDate',
                render: (text) => text ? dayjs(text.replace("+00:00", "+07:00"))
                    .tz('Asia/Bangkok')
                    .format('DD/MM/YYYY HH:mm:ss') : '-',
                sorter: (a, b) => {
                    if (!a.droneDate) return -1;
                    if (!b.droneDate) return 1;
                    return moment(a.droneDate).unix() - moment(b.droneDate).unix();
                },
                filters: Array.from(new Set(droneData.map((item) =>
                    moment(item.droneDate).format("DD/MM/YYYY")))).map(
                        (date) => ({ text: date, value: date })
                    ),
                onFilter: (value, record) =>
                    moment(record.droneDate).format("DD/MM/YYYY") === value
            },
            {
                title: 'Drone ID',
                dataIndex: 'droneId',
                key: 'droneId',
                sorter: (a, b) => a.droneId.localeCompare(b.droneId),
                filters: Array.from(new Set(droneData.map((item) => item.droneId)))
                    .filter(id => id)
                    .map(id => ({ text: id, value: id })),
                onFilter: (value, record) => record.droneId === value
            },
            {
                title: 'Community ID',
                dataIndex: 'communityId',
                key: 'communityId',
                render: (text) => text || '-',
                sorter: (a, b) => {
                    if (!a.communityId) return -1;
                    if (!b.communityId) return 1;
                    return a.communityId.localeCompare(b.communityId);
                },
                filters: Array.from(new Set(droneData.map((item) => item.communityId)))
                    .filter(id => id)
                    .map(id => ({ text: id, value: id })),
                onFilter: (value, record) => record.communityId === value
            },
            {
                title: 'Area (ไร่)',
                dataIndex: 'area',
                key: 'area',
                render: (text) => text ? Number(text).toFixed(2) : '-',
                sorter: (a, b) => a.area - b.area,
                filters: [
                    { text: '0-10 ไร่', value: '0-10' },
                    { text: '10-50 ไร่', value: '10-50' },
                    { text: '50-100 ไร่', value: '50-100' },
                    { text: 'มากกว่า 100 ไร่', value: '100+' }
                ],
                onFilter: (value, record) => {
                    const area = Number(record.area || 0);
                    switch (value) {
                        case '0-10': return area >= 0 && area <= 10;
                        case '10-50': return area > 10 && area <= 50;
                        case '50-100': return area > 50 && area <= 100;
                        case '100+': return area > 100;
                        default: return true;
                    }
                }
            },
            {
                title: 'Duration',
                dataIndex: 'duration',
                key: 'duration',
                render: (text) => text || '-',
                sorter: (a, b) => {
                    const getMinutes = (duration) => {
                        if (!duration) return 0;
                        const match = duration.match(/(\d+)\s*นาที/);
                        return match ? parseInt(match[1]) : 0;
                    };
                    return getMinutes(a.duration) - getMinutes(b.duration);
                },
                filters: [
                    { text: '0-5 นาที', value: '0-5' },
                    { text: '5-10 นาที', value: '5-10' },
                    { text: '10-30 นาที', value: '10-30' },
                    { text: 'มากกว่า 30 นาที', value: '30+' }
                ],
                onFilter: (value, record) => {
                    const getMinutes = (duration) => {
                        if (!duration) return 0;
                        const match = duration.match(/(\d+)\s*นาที/);
                        return match ? parseInt(match[1]) : 0;
                    };
                    const minutes = getMinutes(record.duration);
                    switch (value) {
                        case '0-5': return minutes >= 0 && minutes <= 5;
                        case '5-10': return minutes > 5 && minutes <= 10;
                        case '10-30': return minutes > 10 && minutes <= 30;
                        case '30+': return minutes > 30;
                        default: return true;
                    }
                }
            },
            {
                title: 'Province',
                dataIndex: 'province',
                key: 'province',
                sorter: (a, b) => a.province.localeCompare(b.province),
                filters: Array.from(new Set(droneData.map((item) => item.province)))
                    .filter(province => province)
                    .map(province => ({ text: province, value: province })),
                onFilter: (value, record) => record.province === value
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                sorter: (a, b) => a.status.localeCompare(b.status),
                filters: Array.from(new Set(droneData.map((item) => item.status)))
                    .filter(status => status)
                    .map(status => ({ text: status, value: status })),
                onFilter: (value, record) => record.status === value
            },
            // {
            //     title: 'Map',
            //     key: 'map',
            //     render: (_, record) => (
            //         record.coordinates ? (
            //             <CoordinateCell 
            //                 coordinates={record.coordinates} 
            //                 record={record}
            //             />
            //         ) : null
            //     )
            // },
            {
                title: 'Actions',
                key: 'actions',
                render: (_, record) =>
                    userRole === 'admin' ? (
                        <Space>
                            <Button
                                type="link"
                                onClick={() => {
                                    setEditingRecord(record);
                                    form.setFieldsValue({
                                        ...record,
                                        droneDate: record.droneDate ? dayjs(record.droneDate) : null
                                    });
                                    setPoints(record.coordinates?.map(([lat, lng]) => [lng, lat]) || []);
                                    setFormArea(record.area || 0);
                                    setFlightPath(record.flightPath || []);
                                    setIsModalVisible(true);
                                }}
                                icon={<EditOutlined />}
                            >
                                Edit
                            </Button>
                            <Button
                                type="link"
                                danger
                                onClick={() => {
                                    setDroneToDelete(record);
                                    setDeleteModalVisible(true);
                                }}
                                icon={<DeleteOutlined />}
                            >
                                Delete
                            </Button>
                        </Space>
                    ) : null
            }
        ];

        return (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 className="text-xl font-semibold" style={{ color: '#165B33', margin: 0 }}>
                        รายการข้อมูลโดรนทั้งหมด
                    </h2>
                </div>
                <Table
                    columns={columns}
                    dataSource={droneData}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: true }}
                    pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} items`
                    }}
                />
            </div>
        );
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const coordinates = points.map(([lng, lat]) => [lat, lng]);
            const droneData = {
                ...values,
                area: formArea,
                coordinates,
                droneSpeed,
                flightPath: flightPath,
                droneDate: values.droneDate
                    ? dayjs(values.droneDate)
                        .format('YYYY-MM-DD HH:mm:ss')
                        .replace("+00:00", "+07:00")
                    : null,
            };
            if (editingRecord) {
                droneData.id = values.id;
                delete droneData.droneDate;
                await updateDrone(editingRecord.id, droneData);
                window.location.reload();
            } else {
                await addDrone(droneData);
            }
            handleModalCancel();
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                console.error('Error saving drone:', error);
                message.error('Failed to save drone');
            }
        }
    };

    const addDrone = async (droneData) => {
        try {
            const { data, error } = await supabase
                .from('drones')
                .insert([droneData])
                .select()
                .single();
            if (error) throw error;
            setDrones(prev => [...prev, data]);
            message.success('Drone added successfully');
            return data;
        } catch (error) {
            console.error('Error adding drone:', error);
            message.error('Failed to add drone');
            throw error;
        }
    };

    const updateDrone = async (id, droneData) => {
        try {
            const { data, error } = await supabase
                .from('drones')
                .update(droneData)
                .eq('id', id)
                .select()
                .single();
            if (error) throw error;
            setDrones(prev => prev.map(drone =>
                drone.id === id ? data : drone
            ));
            message.success('Drone updated successfully');
            return data;
        } catch (error) {
            console.error('Error updating drone:', error);
            message.error('Failed to update drone');
            throw error;
        }
    };

    const deleteDrone = async (id) => {
        try {
            const { error } = await supabase
                .from('drones')
                .delete()
                .eq('id', id);
            if (error) throw error;
            setDrones(prev => prev.filter(drone => drone.id !== id));
            message.success('Drone deleted successfully');
        } catch (error) {
            console.error('Error deleting drone:', error);
            message.error('Failed to delete drone');
        }
    };

    const handleDeleteConfirm = () => {
        if (droneToDelete) {
            deleteDrone(droneToDelete.id);
            setDeleteModalVisible(false);
            setDroneToDelete(null);
        }
    };

    const SummaryCards = () => {
        const [summaryData, setSummaryData] = useState({
            totalArea: 0,
            totalFlights: 0,
            totalDrones: 0,
            totalCommunities: 0
        });

        const fetchSummaryData = async () => {
            try {
                let allData = [];
                let start = 0;
                const pageSize = 1000;
                let hasMore = true;

                while (hasMore) {
                    const { data, error, count } = await supabase
                        .from('drones')
                        .select('*', { count: 'exact' })
                        .range(start, start + pageSize - 1);

                    if (error) throw error;

                    if (data) {
                        allData = [...allData, ...data];
                    }

                    start += pageSize;
                    hasMore = start < count;
                }

                // คำนวณข้อมูลสรุป
                const totalArea = allData.reduce((sum, drone) => sum + (Number(drone.area) || 0), 0);
                const totalFlights = allData.length;
                const uniqueDrones = new Set(allData.map(drone => drone.droneId)).size;
                const uniqueCommunities = new Set(allData.map(drone => drone.communityId).filter(Boolean)).size;

                setSummaryData({
                    totalArea: totalArea.toFixed(2),
                    totalFlights,
                    totalDrones: uniqueDrones,
                    totalCommunities: uniqueCommunities
                });
            } catch (error) {
                console.error('Error fetching summary data:', error);
                message.error('Failed to fetch summary data');
            }
        };

        useEffect(() => {
            fetchSummaryData();
        }, []);

        const cards = [
            {
                title: 'จำนวนไร่ทั้งหมด',
                value: summaryData.totalArea,
                unit: 'ไร่',
                color: '#165B33',
                icon: '📊'
            },
            {
                title: 'จำนวนเที่ยวบินทั้งหมด',
                value: summaryData.totalFlights,
                unit: 'เที่ยว',
                color: '#2E7D32',
                icon: '✈️'
            },
            {
                title: 'จำนวนโดรนทั้งหมด',
                value: summaryData.totalDrones,
                unit: 'ลำ',
                color: '#388E3C',
                icon: '🚁'
            },
            {
                title: 'จำนวน Community ทั้งหมด',
                value: summaryData.totalCommunities,
                unit: 'ชุมชน',
                color: '#43A047',
                icon: '🏘️'
            }
        ];

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {cards.map((card, index) => (
                    <div
                        key={index}
                        className="bg-white rounded-lg shadow-sm p-6"
                        style={{ borderTop: `4px solid ${card.color}` }}
                    >
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold text-gray-700">{card.title}</h3>
                            <span className="text-2xl">{card.icon}</span>
                        </div>
                        <div className="flex items-baseline">
                            <span className="text-3xl font-bold" style={{ color: card.color }}>
                                {card.value}
                            </span>
                            <span className="ml-2 text-gray-600">{card.unit}</span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div style={{ filter: isLoginModalVisible ? 'blur(8px)' : 'none' }}>
                <PageHeader>
                    <HeaderContent />
                </PageHeader>

                <div className="container mx-auto p-4">
                    {userRole === 'admin' && <SummaryCards />}
                    <AreaSummaryTable />
                    {userRole === 'admin' && <DroneList />}

                    <Modal
                        title={
                            <div style={{ color: '#165B33', borderBottom: '2px solid #165B33', paddingBottom: '8px' }}>
                                {editingRecord ? "Edit Drone" : "Add New Drone"}
                            </div>
                        }
                        open={isModalVisible}
                        onOk={handleModalOk}
                        onCancel={handleModalCancel}
                        width={1200}
                        footer={[
                            <Button key="cancel" onClick={handleModalCancel}>
                                Cancel
                            </Button>,
                            <StyledButton key="submit" type="primary" onClick={handleModalOk}>
                                {editingRecord ? 'Update' : 'Add'} Drone
                            </StyledButton>
                        ]}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={editingRecord || initialDroneData}
                            className="mt-4"
                        >
                            <div className="grid grid-cols-12 gap-6">
                                <div className={editingRecord ? "col-span-12" : "col-span-5"}>
                                    <div className="bg-gray-50 p-6 rounded-lg">
                                        <h3 className="text-lg font-semibold mb-4 text-gray-700">Drone Information</h3>
                                        <Form.Item
                                            label="Search Drone ID"
                                            style={{ marginBottom: '20px' }}
                                        >
                                            <Input.Search
                                                placeholder="Enter Drone ID to search"
                                                onSearch={handleDroneSearch}
                                                enterButton
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                        {
                                            !editingRecord && (
                                                <Form.Item
                                                    name="droneDate"
                                                    label="Drone Date"
                                                    rules={[{ required: true, message: 'Please select drone date!' }]}
                                                >
                                                    <DatePicker
                                                        showTime
                                                        format="DD/MM/YYYY HH:mm:ss"
                                                        placeholder="Select date and time"
                                                    />
                                                </Form.Item>
                                            )
                                        }
                                        <Row gutter={[16, 16]}>
                                            <Col>
                                                <Form.Item
                                                    name="droneId"
                                                    label="Drone ID"
                                                    rules={[{ required: true, message: 'Please input drone ID!' }]}
                                                >
                                                    <Input placeholder="Enter drone ID" />
                                                </Form.Item>
                                            </Col>
                                            <Col>
                                                <Form.Item
                                                    name="droneCode"
                                                    label="Drone Code"
                                                    rules={[{ required: true, message: 'Please input drone code!' }]}
                                                >
                                                    <Input placeholder="Enter drone code" />
                                                </Form.Item>
                                            </Col>
                                            <Col>
                                                <Form.Item
                                                    name="droneName"
                                                    label="Drone Name"
                                                    rules={[{ required: true, message: 'Please input drone name!' }]}
                                                >
                                                    <Input placeholder="Enter drone name" />
                                                </Form.Item>
                                            </Col>
                                            <Col>
                                                <Form.Item
                                                    name="droneKPI"
                                                    label="Drone KPI"
                                                >
                                                    <Input placeholder="Enter drone KPI" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={11}>
                                                <Form.Item
                                                    name="province"
                                                    label="Province"
                                                    rules={[{ required: true, message: 'Please select province!' }]}
                                                >
                                                    <Select placeholder="Select province">
                                                        <Select.Option value="กรุงเทพมหานคร">กรุงเทพมหานคร</Select.Option>
                                                        <Select.Option value="กระบี่">กระบี่</Select.Option>
                                                        <Select.Option value="กาญจนบุรี">กาญจนบุรี</Select.Option>
                                                        <Select.Option value="กาฬสินธุ์">กาฬสินธุ์</Select.Option>
                                                        <Select.Option value="กำแพงเพชร">กำแพงเพชร</Select.Option>
                                                        <Select.Option value="ขอนแก่น">ขอนแก่น</Select.Option>
                                                        <Select.Option value="จันทบุรี">จันทบุรี</Select.Option>
                                                        <Select.Option value="ฉะเชิงเทรา">ฉะเชิงเทรา</Select.Option>
                                                        <Select.Option value="ชลบุรี">ชลบุรี</Select.Option>
                                                        <Select.Option value="ชัยนาท">ชัยนาท</Select.Option>
                                                        <Select.Option value="ชัยภูมิ">ชัยภูมิ</Select.Option>
                                                        <Select.Option value="ชุมพร">ชุมพร</Select.Option>
                                                        <Select.Option value="เชียงราย">เชียงราย</Select.Option>
                                                        <Select.Option value="เชียงใหม่">เชียงใหม่</Select.Option>
                                                        <Select.Option value="ตรัง">ตรัง</Select.Option>
                                                        <Select.Option value="ตราด">ตราด</Select.Option>
                                                        <Select.Option value="ตาก">ตาก</Select.Option>
                                                        <Select.Option value="นครนายก">นครนายก</Select.Option>
                                                        <Select.Option value="นครปฐม">นครปฐม</Select.Option>
                                                        <Select.Option value="นครพนม">นครพนม</Select.Option>
                                                        <Select.Option value="นครราชสีมา">นครราชสีมา</Select.Option>
                                                        <Select.Option value="นครศรีธรรมราช">นครศรีธรรมราช</Select.Option>
                                                        <Select.Option value="นครสวรรค์">นครสวรรค์</Select.Option>
                                                        <Select.Option value="นนทบุรี">นนทบุรี</Select.Option>
                                                        <Select.Option value="นราธิวาส">นราธิวาส</Select.Option>
                                                        <Select.Option value="น่าน">น่าน</Select.Option>
                                                        <Select.Option value="บึงกาฬ">บึงกาฬ</Select.Option>
                                                        <Select.Option value="บุรีรัมย์">บุรีรัมย์</Select.Option>
                                                        <Select.Option value="ปทุมธานี">ปทุมธานี</Select.Option>
                                                        <Select.Option value="ประจวบคีรีขันธ์">ประจวบคีรีขันธ์</Select.Option>
                                                        <Select.Option value="ปราจีนบุรี">ปราจีนบุรี</Select.Option>
                                                        <Select.Option value="ปัตตานี">ปัตตานี</Select.Option>
                                                        <Select.Option value="พระนครศรีอยุธยา">พระนครศรีอยุธยา</Select.Option>
                                                        <Select.Option value="พะเยา">พะเยา</Select.Option>
                                                        <Select.Option value="พังงา">พังงา</Select.Option>
                                                        <Select.Option value="พัทลุง">พัทลุง</Select.Option>
                                                        <Select.Option value="พิจิตร">พิจิตร</Select.Option>
                                                        <Select.Option value="พิษณุโลก">พิษณุโลก</Select.Option>
                                                        <Select.Option value="เพชรบุรี">เพชรบุรี</Select.Option>
                                                        <Select.Option value="เพชรบูรณ์">เพชรบูรณ์</Select.Option>
                                                        <Select.Option value="แพร่">แพร่</Select.Option>
                                                        <Select.Option value="ภูเก็ต">ภูเก็ต</Select.Option>
                                                        <Select.Option value="มหาสารคาม">มหาสารคาม</Select.Option>
                                                        <Select.Option value="มุกดาหาร">มุกดาหาร</Select.Option>
                                                        <Select.Option value="แม่ฮ่องสอน">แม่ฮ่องสอน</Select.Option>
                                                        <Select.Option value="ยโสธร">ยโสธร</Select.Option>
                                                        <Select.Option value="ยะลา">ยะลา</Select.Option>
                                                        <Select.Option value="ร้อยเอ็ด">ร้อยเอ็ด</Select.Option>
                                                        <Select.Option value="ระนอง">ระนอง</Select.Option>
                                                        <Select.Option value="ระยอง">ระยอง</Select.Option>
                                                        <Select.Option value="ราชบุรี">ราชบุรี</Select.Option>
                                                        <Select.Option value="ลพบุรี">ลพบุรี</Select.Option>
                                                        <Select.Option value="ลำปาง">ลำปาง</Select.Option>
                                                        <Select.Option value="ลำพูน">ลำพูน</Select.Option>
                                                        <Select.Option value="เลย">เลย</Select.Option>
                                                        <Select.Option value="ศรีสะเกษ">ศรีสะเกษ</Select.Option>
                                                        <Select.Option value="สกลนคร">สกลนคร</Select.Option>
                                                        <Select.Option value="สงขลา">สงขลา</Select.Option>
                                                        <Select.Option value="สตูล">สตูล</Select.Option>
                                                        <Select.Option value="สมุทรปราการ">สมุทรปราการ</Select.Option>
                                                        <Select.Option value="สมุทรสงคราม">สมุทรสงคราม</Select.Option>
                                                        <Select.Option value="สมุทรสาคร">สมุทรสาคร</Select.Option>
                                                        <Select.Option value="สระแก้ว">สระแก้ว</Select.Option>
                                                        <Select.Option value="สระบุรี">สระบุรี</Select.Option>
                                                        <Select.Option value="สิงห์บุรี">สิงห์บุรี</Select.Option>
                                                        <Select.Option value="สุโขทัย">สุโขทัย</Select.Option>
                                                        <Select.Option value="สุพรรณบุรี">สุพรรณบุรี</Select.Option>
                                                        <Select.Option value="สุราษฎร์ธานี">สุราษฎร์ธานี</Select.Option>
                                                        <Select.Option value="สุรินทร์">สุรินทร์</Select.Option>
                                                        <Select.Option value="หนองคาย">หนองคาย</Select.Option>
                                                        <Select.Option value="หนองบัวลำภู">หนองบัวลำภู</Select.Option>
                                                        <Select.Option value="อ่างทอง">อ่างทอง</Select.Option>
                                                        <Select.Option value="อำนาจเจริญ">อำนาจเจริญ</Select.Option>
                                                        <Select.Option value="อุดรธานี">อุดรธานี</Select.Option>
                                                        <Select.Option value="อุตรดิตถ์">อุตรดิตถ์</Select.Option>
                                                        <Select.Option value="อุทัยธานี">อุทัยธานี</Select.Option>
                                                        <Select.Option value="อุบลราชธานี">อุบลราชธานี</Select.Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col>
                                                <Form.Item
                                                    name="communityId"
                                                    label="Community ID"
                                                >
                                                    <Input placeholder="Enter community ID" />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Row gutter={[16, 16]}>
                                            <Col span={11}>
                                                <Form.Item
                                                    name="status"
                                                    label="Status"
                                                    rules={[{ required: true, message: 'Please select status!' }]}
                                                >
                                                    <Select placeholder="Select status">
                                                        <Select.Option value="active">Active</Select.Option>
                                                        <Select.Option value="inactive">Inactive</Select.Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col>
                                                <Form.Item
                                                    name="companyDrone"
                                                    label="Company Drone"
                                                >
                                                    <Input placeholder="Enter company name" />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item
                                            name="coordinates"
                                            label="Coordinates"
                                        >
                                            <Input.TextArea rows={4} placeholder="Enter coordinates" />
                                        </Form.Item>
                                    </div>
                                </div>

                                {!editingRecord && (
                                    <div className="col-span-7">
                                        <div className="bg-white p-6 rounded-lg shadow-sm">
                                            <h3 className="text-lg font-semibold mb-4 text-gray-700">Map Selection</h3>
                                            <div className="flex gap-3 mb-4">
                                                <Input.Search
                                                    placeholder="Search coordinates (lat,lng)"
                                                    value={formSearchCoord}
                                                    onChange={(e) => setFormSearchCoord(e.target.value)}
                                                    onSearch={handleFormSearch}
                                                    className="flex-1"
                                                />
                                            </div>
                                            <div className="grid grid-cols-3 gap-4 mb-4">
                                                <Form.Item
                                                    label="Row Spacing (meters)"
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Input
                                                        type="number"
                                                        value={rowSpacing}
                                                        onChange={(e) => setRowSpacing(Number(e.target.value))}
                                                        min={1}
                                                        suffix="m"
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label="Flight Angle (degrees)"
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Input
                                                        type="number"
                                                        value={flightAngle}
                                                        onChange={(e) => setFlightAngle(Number(e.target.value))}
                                                        min={0}
                                                        max={360}
                                                        suffix="°"
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label="ความเร็วของโดรน (เมตร/วินาที)"
                                                    name="droneSpeed"
                                                    rules={[{ required: true, message: 'กรุณาระบุความเร็วของโดรน' }]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        step={0.1}
                                                        style={{ width: '100%' }}
                                                        onChange={(value) => {
                                                            form.setFieldsValue({ droneSpeed: value });
                                                            if (flightPath && flightPath.length > 1) {
                                                                handleCalculateDuration();
                                                            }
                                                        }}
                                                    />
                                                </Form.Item>
                                            </div>
                                            <Form.Item
                                                label="ระยะเวลาบิน"
                                                name="duration"
                                                rules={[{ required: true, message: 'กรุณาคำนวณระยะเวลาบิน' }]}
                                            >
                                                <Space.Compact style={{ width: '100%' }}>
                                                    <Input 
                                                        readOnly 
                                                        style={{ width: 'calc(100% - 80px)' }}
                                                        value={form.getFieldValue('duration')}
                                                    />
                                                    <Button 
                                                        type="primary" 
                                                        onClick={handleCalculateDuration}
                                                        disabled={!flightPath || flightPath.length < 2}
                                                        style={{ width: '80px' }}
                                                    >
                                                        คำนวณ
                                                    </Button>
                                                </Space.Compact>
                                            </Form.Item>
                                            <Form.Item
                                                name="area"
                                                label="Area"
                                                style={{ marginBottom: 16 }}
                                            >
                                                <Input disabled suffix="ไร่" />
                                            </Form.Item>
                                            <Row gutter={16} justify="space-between">
                                                <Col span={12}>
                                                    <Button
                                                        type="primary"
                                                        onClick={generateFlightPath}
                                                        icon={<PlusOutlined />}
                                                        className="mb-4 w-full"
                                                        style={{
                                                            background: '#165B33',
                                                            height: '40px',
                                                            fontSize: '16px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        Generate Flight Path
                                                    </Button>
                                                </Col>
                                                <Col>
                                                    <Button
                                                        type="primary"
                                                        danger
                                                        onClick={clearFormMap}
                                                        icon={<DeleteOutlined />}
                                                        className="mb-4 w-full"
                                                        style={{
                                                            height: '40px',
                                                            fontSize: '16px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        ล้างแผนที่
                                                    </Button>
                                                </Col>
                                            </Row>
                                            <div
                                                ref={formMapContainer}
                                                style={{ width: '100%', height: '400px' }}
                                                className="rounded-lg overflow-hidden border border-gray-200"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Form>
                    </Modal>

                    <Modal
                        title={<div style={{ color: '#ff4d4f' }}>Confirm Delete</div>}
                        open={deleteModalVisible}
                        onOk={handleDeleteConfirm}
                        onCancel={() => {
                            setDeleteModalVisible(false);
                            setDroneToDelete(null);
                        }}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{
                            danger: true,
                            style: {
                                paddingLeft: '24px',
                                paddingRight: '24px'
                            }
                        }}
                    >
                        <p>Are you sure you want to delete this drone?</p>
                        {droneToDelete && (
                            <div style={{ marginTop: '8px', color: '#666' }}>
                                <p>Drone ID: {droneToDelete.droneId} ID: {droneToDelete.id}</p>
                                <p>Drone Name: {droneToDelete.droneName}</p>
                            </div>
                        )}
                    </Modal>
                </div>
            </div>

            {isLoginModalVisible && (
                <BlurOverlay>
                    <LoginContainer>
                        <h2 style={{
                            color: '#165B33',
                            marginBottom: '24px',
                            textAlign: 'center',
                            fontSize: '24px',
                            fontWeight: 'bold'
                        }}>
                            Login
                        </h2>
                        <Form
                            form={loginForm}
                            onFinish={handleLogin}
                            layout="vertical"
                        >
                            <Form.Item
                                name="username"
                                label="Username"
                                rules={[{ required: true, message: 'Please input your username!' }]}
                            >
                                <Input placeholder="Enter username" />
                            </Form.Item>
                            <Form.Item
                                name="password"
                                label="Password"
                                rules={[{ required: true, message: 'Please input your password!' }]}
                            >
                                <Input.Password placeholder="Enter password" />
                            </Form.Item>
                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    style={{
                                        width: '100%',
                                        background: '#165B33',
                                        height: '40px',
                                        fontSize: '16px'
                                    }}
                                >
                                    Login
                                </Button>
                            </Form.Item>
                        </Form>
                    </LoginContainer>
                </BlurOverlay>
            )}
        </div>
    );
}
